import { ComponentInternalInstance, computed, getCurrentInstance, onUnmounted, watch } from '@vue/composition-api';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入工具类
import meetingApp from '@/renderer/scripts/utils/meeting';
import smh from '@/renderer/scripts/smh';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import tips from '@/renderer/scripts/utils/tips';
import { PAY_SOURCE, openTeamPayDialog } from '@/renderer/scripts/composition/dialogHandler';
import { LOCATION_MAP } from '@/common/reporter/utils';
interface ShareOptions {
  name: string;
  path: string[];
  spaceId: string;
  spaceOrgId?: number;
  previewByDoc?: boolean;
  btnLocation?: string;
}
type Handler = {
  methods: {
    share: (_action?: 'preview' | 'edit' | string, options?: ShareOptions) => void;
    shareUrlInWemeet: (
      params: {
        url: string;
        code?: string;
        name: string;
      },
      cb?: Function,
    ) => Promise<void>;
  };
};
export default function handler(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const routeFrom = computed(() => vm.$route.query.from as string); // 当前页面的来源路由
  const { $store } = vm;
  const methods = {
    async getSingleFileShareLink(action: 'preview' | 'edit' | string, options?: ShareOptions) {
      const { $route } = vm;
      const filename = options?.name || ($route.query.name as string);
      const filepath = options?.path.join('/') || [$route.query.path, filename].filter(v => v).join('/');
      let pageType = action;
      if (action === 'preview') {
        if (options?.previewByDoc) {
          pageType = 'doc';
        } else {
          pageType = 'media';
        }
      } else if (action === 'edit') {
        pageType = 'edit';
      } else {
        pageType = $route.name as string;
      }
      const spaceId =
        options?.spaceId || ($route.query.spaceId as string) || (await spaceATM.getPersonalSpaceIdAsync());
      const params = {
        name: filename,
        pageType,
        directoryInfo: {
          spaceId,
          path: filepath,
          ...($route.query.historyId && {
            versionId: +$route.query.historyId,
          }),
          type: 'file',
          spaceOrgId: options?.spaceOrgId || $store.getters.orgId,
        },
        expireTime: dayjs().add(1, 'day').format('YYYY-MM-DD HH:mm:ss'),
        isPermanent: false,
        linkToLatestVersion: true,
        canPreview: true,
        canDownload: true,
        canSaveToNetDisc: true,
        canModify: action === 'edit' || $route.name === 'edit',
        shareTraffic: 0,
      };
      try {
        const { data } = await smh.userAPI.getSingleFileShareLink(params);
        return data.url;
      } catch (error: MsgError) {
        if (error?.code === 'OrganizationShareLinkLimitReached') {
          openTeamPayDialog(PAY_SOURCE.CREATE_SHARE_LINK);
        } else {
          tips.error(error as MsgError);
        }
        return '';
      }
    },
    async share(_action?: 'preview' | 'edit' | string, options?: ShareOptions) {
      const { $route } = vm;
      const action = _action || $route.name;
      const filename = options?.name || $route.query.name;
      const actionStr = action === 'edit' ? i18n_t('编辑') : i18n_t('预览');
      const desc = i18n_t('在线{actionStr}文件"{filename}"', {
        actionStr,
        filename,
      });
      const btnLocation = options?.btnLocation || '';
      const routeName = $route.name;
      try {
        let url = '';
        if (!$store.getters.isLogined || routeFrom.value === 'share') {
          url = window.location.href;
        } else {
          url = await this.getSingleFileShareLink(action as string, options);
        }
        if (!url) return;
        const shareParams = {
          shareEnable: false,
          desc,
          // IM分享卡片描述
          notifyDesc: desc,
          // 通知弹窗描述
          pcUrl: url.replace('cofile.net', 'pan.tencent.com').replace(/dev\.|pre\./, ''),
          // 桌面端分享url，测试应用需要test前缀
          mobileUrl: url.replace('cofile.net', 'pan.tencent.com').replace(/dev\.|pre\./, ''), // 移动端分享url，测试应用需要test前缀
        };

        await meetingApp.share(shareParams, (shareResult: string) => {
          reporter.report(`${_action}_invite`, {
            invite_location: LOCATION_MAP[routeName as string] || routeName || '',
            click_type: btnLocation,
            file_name: filename,
            result: shareResult,
          });
        });
      } catch (error) {
        tips.error(i18n_t('邀请分享失败'));
        logger.ui.error('[wemeeting setShareOpenAppConfig]', JSON.stringify(error));
      }
    },
    resetShareIcon() {
      meetingApp.resetShare();
    },
    async shareUrlInWemeet(
      params: {
        url: string;
        code?: string;
        name: string;
      },
      cb?: Function,
    ) {
      try {
        const desc = params.code
          ? i18n_t('输入提取码"{slot1}"访问分享的加密文件', {
              slot1: params.code,
            })
          : i18n_t('邀请您查看分享的文件');
        const shareParams = {
          shareEnable: true,
          desc,
          // IM分享卡片描述
          notifyDesc: desc,
          // 通知弹窗描述
          pcUrl: params.url.replace('cofile.net', 'pan.tencent.com').replace(/dev\.|pre\./, ''),
          // 测试应用需要test前缀
          mobileUrl: params.url.replace('cofile.net', 'pan.tencent.com').replace(/dev\.|pre\./, ''), // 测试应用需要test前缀
        };

        await meetingApp.share(shareParams, (shareResult: string) => {
          cb && cb(shareResult);
        });
        // 分享完毕后重置会中分享 url
        methods.resetShareIcon();
      } catch (error) {
        tips.error(i18n_t('邀请分享失败'));
        logger.ui.error('[wemeeting shareInWemeet]', JSON.stringify(error));
      }
    },
  };
  return {
    methods,
  };
}
